import { translateOr, useTranslate } from "@/shared";
import { Button, Pagination } from "@/ui";
import { CheckCircle2, Download, FileSpreadsheet, Info, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useFacility } from "@/shared/hooks/use-facility";
import { useInstallationSolutions } from "../../hooks/use-installation-solutions";
import { useSolutionTemplateUpload } from "../../hooks/use-solution-template-upload";
import type { InstallationPlanTemplateEntry, InstallationPlanScopeEntry } from "../../types/installation-plan";
import type { GeographyDetails } from "../../types/project";
import { hasAcceptedExtension } from "../../utils/file-validation";
import { IngestionStatusBlocks } from "../IngestionStatusBlocks";
import { StepSectionCard } from "../StepSectionCard";

export type TemplateValue = InstallationPlanTemplateEntry[];

const DEFAULT_PAGE_SIZE = 10;

export function isTemplateStepValid(value: TemplateValue, uniqueSolutionCodes: string[]): boolean {
  if (uniqueSolutionCodes.length === 0) return false;
  const uploadedBySolution = new Map(value.map((entry) => [entry.solutionCode, entry.uploaded]));
  return uniqueSolutionCodes.every((code) => uploadedBySolution.get(code));
}

interface SolutionTemplateCardProps {
  planId: string | undefined;
  solutionCode: string;
  solutionName: string;
  assignedSites: Array<{ id: string; name: string }>;
  uploaded: boolean;
  locked: boolean;
  onUploaded: () => void;
  onBusyChange: (solutionCode: string, isBusy: boolean) => void;
}

/** The dropzone's one-line status caption -- uploaded, mid-validation, or the initial prompt. */
function dropzoneCaption(
  uploaded: boolean,
  status: string,
  t: (key: string) => string,
): string {
  if (uploaded) {
    return translateOr(t, "ES_PM_TEMPLATE_UPLOADED_SHORT", "Uploaded")
  }
  if (status === "validating") {
    return translateOr(t, "ES_PM_VALIDATING", "Validating...")
  }
  return translateOr(t, "ES_PM_DRAG_DROP_HINT", "Drag and drop file here or click below")
}

function SolutionTemplateCard({
  planId,
  solutionCode,
  solutionName,
  assignedSites,
  uploaded,
  locked,
  onUploaded,
  onBusyChange,
}: Readonly<SolutionTemplateCardProps>) {
  const { t } = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [invalidFileMessage, setInvalidFileMessage] = useState<string | undefined>(undefined);
  const {
    status,
    errorCount,
    errorMessage,
    errorIsGuidance,
    validatedFile,
    previewFile,
    previewHasErrors,
    downloadPreview,
    downloadTemplate,
    uploadAndValidate,
    createTemplate,
    isBusy,
  } = useSolutionTemplateUpload(planId, solutionCode);
  const processedFileRef = useRef<typeof validatedFile>(null);

  useEffect(() => {
    onBusyChange(solutionCode, isBusy);
  }, [isBusy, solutionCode, onBusyChange]);

  // `validatedFile` only lands in this render's props once the hook's own
  // state has actually updated, so reacting to it here (rather than chaining
  // straight off the upload promise) avoids calling `createTemplate` against
  // a stale closure where it hadn't been set yet.
  useEffect(() => {
    if (validatedFile && validatedFile !== processedFileRef.current) {
      processedFileRef.current = validatedFile;
      void createTemplate().then((created) => {
        if (created) onUploaded();
      });
    }
  }, [validatedFile, createTemplate, onUploaded]);

  return (
    <div className="livelihood-card p-5">
      <div className="grid gap-4 sm:grid-cols-[minmax(0,220px)_1fr]">
        <div>
          <p className="font-semibold text-primary">{solutionName}</p>
          <span
            className={
              "mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium " +
              (uploaded ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")
            }
          >
            {uploaded ? <CheckCircle2 className="size-3" /> : null}
            {uploaded
              ? translateOr(t, "ES_PM_TEMPLATE_UPLOADED", "Template uploaded")
              : translateOr(t, "ES_PM_TEMPLATE_NOT_UPLOADED", "Not uploaded yet")}
          </span>
          <p className="mt-1 text-xs font-medium text-muted-foreground">
            {translateOr(t, "ES_PM_ASSIGNED_SITES", "Assigned Sites")} ({assignedSites.length})
          </p>
          <ul className="mt-1 h-30 space-y-0.5 overflow-y-auto pr-1 text-sm text-foreground">
            {assignedSites.map((site) => (
              <li key={site.id}>{site.name}</li>
            ))}
          </ul>
        </div>
        <div className="flex min-h-[110px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-input bg-card p-4 text-center">
          <Upload className="size-5 text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold text-foreground">
              {translateOr(t, "ES_PM_TEMPLATE_FOR", "Template for")} {solutionName}
            </p>
            <p className="text-xs text-muted-foreground">{dropzoneCaption(uploaded, status, t)}</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate} disabled={isBusy}>
              <Download className="size-4" />
              {translateOr(t, "ES_PM_DOWNLOAD_TEMPLATE", "Download Template")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={isBusy || locked}
            >
              <Upload className="size-4" />
              {translateOr(t, "ES_PM_UPLOAD_TEMPLATE", "Upload Template")}
            </Button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file && !locked) {
                if (hasAcceptedExtension(file, ".xlsx")) {
                  setInvalidFileMessage(undefined);
                  void uploadAndValidate(file);
                } else {
                  setInvalidFileMessage(
                    translateOr(t, "ES_PM_INVALID_FILE_TYPE", "Please upload a valid .xlsx file"),
                  );
                }
              }
              event.target.value = "";
            }}
          />
          {previewFile ? (
            <div className="w-full space-y-1">
              {previewHasErrors ? (
                <p className="text-xs font-medium text-destructive">
                  {translateOr(t, "ES_PM_VALIDATION_ERRORS", "Found errors in the uploaded file")}: {errorCount}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant={previewHasErrors ? "destructive" : "outline"}
                  size="sm"
                  onClick={downloadPreview}
                >
                  <Download className="size-4" />
                  {previewHasErrors
                    ? translateOr(t, "ES_PM_PREVIEW_FILE_WITH_ERRORS", "Preview File (view errors)")
                    : translateOr(t, "ES_PM_PREVIEW_FILE", "Preview File")}
                </Button>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="size-3.5 shrink-0" />
                  {translateOr(
                    t,
                    "ES_PM_PREVIEW_FILE_LOST_ON_NAVIGATE",
                    "This preview won't be available once you leave this page",
                  )}
                </span>
              </div>
            </div>
          ) : null}
          <IngestionStatusBlocks
            compact
            status={invalidFileMessage ? "error" : status}
            errorMessage={invalidFileMessage ?? errorMessage}
            isGuidance={invalidFileMessage ? true : errorIsGuidance}
          />
        </div>
      </div>
    </div>
  );
}

interface TemplateStepProps {
  planId: string | undefined;
  planCode?: string;
  scope: InstallationPlanScopeEntry[];
  /** The parent project's geography — only used to look the plan's sites up by name. */
  projectGeography: GeographyDetails;
  value: TemplateValue;
  /** Accepts an updater, not just a value: two solution cards can be uploading at once, and each
   *  resolves from its own promise, so building the next array from the `value` of the render
   *  that started the upload loses whatever the other card finished in the meantime. */
  onChange: Dispatch<SetStateAction<TemplateValue>>;
  onBusyChange?: (isBusy: boolean) => void;
  /** Published plans retain uploaded templates but do not allow replacements. */
  locked?: boolean;
}

export function TemplateStep({
  planId,
  planCode,
  scope,
  projectGeography,
  value,
  onChange,
  onBusyChange,
  locked = false,
}: Readonly<TemplateStepProps>) {
  const { t } = useTranslate();
  // State, not a ref: pagination below must re-render and disable itself while any card is
  // mid-upload, since paging away unmounts that card and strands its in-flight validate/create
  // chain with no effect left to ever clear this.
  const [busySolutionCodes, setBusySolutionCodes] = useState<Set<string>>(new Set());
  const isAnyUploadBusy = busySolutionCodes.size > 0;
  const { data: solutions = [] } = useInstallationSolutions();

  const solutionsInScope = useMemo(() => {
    const codes = new Set(scope.filter((entry) => entry.included && entry.solutionCode).map((entry) => entry.solutionCode!));
    return Array.from(codes).map((code) => ({
      code,
      name: solutions.find((solution) => solution.code === code)?.name ?? code,
    }));
  }, [scope, solutions]);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSizeLimit, setPageSizeLimit] = useState(DEFAULT_PAGE_SIZE);
  const totalRecords = solutionsInScope.length;
  const pagedSolutions = useMemo(
    () => solutionsInScope.slice(currentPage * pageSizeLimit, (currentPage + 1) * pageSizeLimit),
    [solutionsInScope, currentPage, pageSizeLimit],
  );

  function handlePageChange(page: number) {
    if (isAnyUploadBusy) return;
    setCurrentPage(page);
  }

  function handlePageSizeChange(size: number) {
    if (isAnyUploadBusy) return;
    setPageSizeLimit(size);
    setCurrentPage(0);
  }

  // A plan's scope stores only facility ids, so the readable site name has to come from
  // facility-service. It is looked up over the project's own blocks — the same search
  // `downloadScopeTemplate` uses to decide which facilities belong to the plan at all. These are
  // real data, not localization keys, so there is nothing to translate them against; the id is
  // kept as the fallback for anything the lookup doesn't cover.
  const { data: facilityData } = useFacility(
    (projectGeography.blocks ?? []).map((block) => block.code),
    { onmReadyOnly: false },
  );
  const siteNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const facility of facilityData?.facilities ?? []) {
      if (facility.facilityId && facility.facilityName) names.set(facility.facilityId, facility.facilityName);
    }
    return names;
  }, [facilityData]);

  function assignedSites(solutionCode: string) {
    return scope
      .filter((entry) => entry.included && entry.solutionCode === solutionCode)
      .map((entry) => ({ id: entry.siteId, name: siteNameById.get(entry.siteId) ?? entry.siteId }));
  }

  function markUploaded(solutionCode: string) {
    onChange((previous) => [
      ...previous.filter((entry) => entry.solutionCode !== solutionCode),
      { solutionCode, uploaded: true },
    ]);
  }

  function handleCardBusyChange(solutionCode: string, isBusy: boolean) {
    setBusySolutionCodes((previous) => {
      if (previous.has(solutionCode) === isBusy) return previous;
      const next = new Set(previous);
      if (isBusy) next.add(solutionCode);
      else next.delete(solutionCode);
      return next;
    });
  }

  useEffect(() => {
    onBusyChange?.(isAnyUploadBusy);
  }, [isAnyUploadBusy, onBusyChange]);

  return (
    <StepSectionCard
      icon={FileSpreadsheet}
      title={translateOr(t, "ES_PM_TEMPLATE", "Template")}
      description={translateOr(
        t,
        "ES_PM_TEMPLATE_DESC",
        "Download, fill in, and upload the IC report template for each solution in this plan",
      )}
    >
      <div className="space-y-4">
        {planCode ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-muted-foreground">
              {translateOr(t, "ES_PM_INSTALLATION_PLAN_CODE", "Installation Plan Code")}:
            </span>
            <span className="font-semibold text-foreground">{planCode}</span>
          </div>
        ) : null}
        {solutionsInScope.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translateOr(t, "ES_PM_NO_SOLUTIONS_IN_SCOPE", "No solutions in scope yet — go back and include a site")}
          </p>
        ) : (
          pagedSolutions.map((solution) => (
            <SolutionTemplateCard
              key={solution.code}
              planId={planId}
              solutionCode={solution.code}
              solutionName={solution.name}
              assignedSites={assignedSites(solution.code)}
              uploaded={value.some((entry) => entry.solutionCode === solution.code && entry.uploaded)}
              locked={locked}
              onUploaded={() => markUploaded(solution.code)}
              onBusyChange={handleCardBusyChange}
            />
          ))
        )}
        {totalRecords > 0 ? (
          // Disabled while any card is mid-upload -- paging away would unmount it and strand its
          // in-flight validate/create chain with nothing left to ever report it done (see
          // handleCardBusyChange above).
          <div
            className={isAnyUploadBusy ? "pointer-events-none opacity-50" : undefined}
            aria-disabled={isAnyUploadBusy}
          >
            <Pagination
              currentPage={currentPage}
              totalRecords={totalRecords}
              pageSizeLimit={pageSizeLimit}
              onNextPage={() => handlePageChange(currentPage + 1)}
              onPrevPage={() => handlePageChange(Math.max(0, currentPage - 1))}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        ) : null}
      </div>
    </StepSectionCard>
  );
}
